import { Card, Typography, Row, Col, Tag, message } from 'antd'
import { CheckCircleFilled } from '@ant-design/icons'
import { useThemeStore, themes, type ThemeConfig } from '@/stores/theme'
import '../../../styles/aurora-page.css'

const { Title, Text } = Typography

export default function ThemeSettings() {
  const { current, setTheme } = useThemeStore()

  const handleSelect = (key: string) => {
    setTheme(key)
    const t = themes.find((item) => item.key === key)
    message.success(`已切换为：${t?.name}`)
  }

  return (
    <div className="aurora-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          页面主题
        </Title>
        <Text style={{ color: 'var(--text-light)', fontSize: 13 }}>
          当前主题：<Tag color={getTagColor(current)}>{themes.find((t) => t.key === current)?.name || '未知'}</Tag>
        </Text>
      </div>

      <div
        className="content-card"
        style={{
          padding: 24,
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        <Row gutter={[16, 16]}>
          {themes.map((theme) => (
            <Col key={theme.key} xs={24} sm={12} md={8} lg={8}>
              <ThemeCard
                theme={theme}
                active={current === theme.key}
                onSelect={() => handleSelect(theme.key)}
              />
            </Col>
          ))}
        </Row>
      </div>
    </div>
  )
}

function ThemeCard({ theme, active, onSelect }: { theme: ThemeConfig; active: boolean; onSelect: () => void }) {
  const { primary, primaryLight, primaryDark, gradient } = theme.colors

  return (
    <div
      onClick={onSelect}
      style={{
        cursor: 'pointer',
        borderRadius: 12,
        border: active ? `2px solid ${primary}` : '2px solid transparent',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        boxShadow: active ? `0 4px 16px ${primary}33` : '0 1px 4px rgba(0,0,0,0.06)',
        transform: active ? 'scale(1.02)' : 'scale(1)',
        position: 'relative',
      }}
    >
      {/* 选中标记 */}
      {active && (
        <div style={{
          position: 'absolute', top: 8, right: 8, zIndex: 2,
          background: primary, color: '#fff', borderRadius: '50%',
          width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
        }}>
          <CheckCircleFilled style={{ fontSize: 24 }} />
        </div>
      )}

      {/* 预览渐变条 */}
      <div style={{
        height: 80,
        background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 40%, ${primaryLight} 100%)`,
        position: 'relative',
      }}>
        {/* 模拟侧边栏 + 内容区 */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: '30%',
          background: 'rgba(0,0,0,0.25)',
        }} />
        <div style={{
          position: 'absolute', left: '30%', top: 0, height: '35%',
          right: 0, background: 'rgba(255,255,255,0.15)',
        }} />
      </div>

      {/* 颜色色块 */}
      <div style={{ padding: '12px 16px', background: 'var(--au-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <Text strong style={{ fontSize: 15 }}>{theme.name}</Text>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ width: 18, height: 18, borderRadius: 4, background: primaryDark }} title="深色" />
            <div style={{ width: 18, height: 18, borderRadius: 4, background: primary }} title="主色" />
            <div style={{ width: 18, height: 18, borderRadius: 4, background: primaryLight }} title="浅色" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Tag style={{ margin: 0, background: primary + '18', color: primary, border: 'none', fontSize: 11 }}>
            主色 {primary}
          </Tag>
          <Tag style={{ margin: 0, background: primaryDark + '18', color: primaryDark, border: 'none', fontSize: 11 }}>
            深色 {primaryDark}
          </Tag>
        </div>
      </div>
    </div>
  )
}

function getTagColor(key: string): string {
  const map: Record<string, string> = {
    'aurora-sage': 'green',
    'mars-green': 'cyan',
    'ginger-yellow': 'gold',
    'peach-pink': 'pink',
    'rose-pink': 'magenta',
    'taro-purple': 'purple',
    'apple-green': 'green',
    'cheese': 'gold',
    'lotus-cyan': 'cyan',
    'starlight-blue': 'blue',
    'mint-green': 'green',
    'cherry-blossom': 'pink',
    'light-cyan': 'cyan',
    'purple-blue': 'purple',
    'bamboo-moon': 'blue',
    'light-fragrance': 'gold',
    'pine-cone-brown': 'brown',
    'fog-purple-gray': 'purple',
    'star-purple': 'purple',
    'moss-green': 'green',
    'haze-blue': 'blue',
    'ou-green': 'green',
    'coral-pink': 'red',
    'wall-green': 'green',
    'turquoise': 'orange',
  }
  return map[key] || 'default'
}
